"use client";

import { Button } from "@/components/ui/button";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ModeToggle } from "@/components/mode-toggle";

const montserrat = Montserrat({
  variable: "--font-montserrat",
  subsets: ["latin"],
});

const sections = [
  { id: "terms-and-conditions", title: "Terms and Conditions", level: 2 },
  { id: "acceptance-of-terms", title: "1. Acceptance of Terms", level: 3 },
  { id: "user-responsibilities", title: "2. User Responsibilities", level: 3 },
  {
    id: "subscription-plans",
    title: "3. Subscription Plans and Payments",
    level: 3,
  },
  { id: "account-termination", title: "4. Account Termination", level: 3 },
  { id: "intellectual-property", title: "5. Intellectual Property", level: 3 },
  {
    id: "privacy-and-data-use-terms",
    title: "6. Privacy and Data Use",
    level: 3,
  },
  {
    id: "limitation-of-liability",
    title: "7. Limitation of Liability",
    level: 3,
  },
  {
    id: "modifications-and-availability",
    title: "8. Modifications and Availability",
    level: 3,
  },
  {
    id: "waiver-and-arbitration",
    title: "9. Waiver of Right to Sue & Binding Arbitration",
    level: 3,
  },
  {
    id: "contact-information-terms",
    title: "10. Contact Information",
    level: 3,
  },
  { id: "privacy-policy", title: "Privacy Policy", level: 2, isPolicy: true },
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    level: 3,
    isPolicy: true,
  },
  {
    id: "payment-processing",
    title: "2. Payment Processing",
    level: 3,
    isPolicy: true,
  },
  {
    id: "cookies-and-tracking",
    title: "3. Cookies and Tracking",
    level: 3,
    isPolicy: true,
  },
  {
    id: "data-retention",
    title: "4. Data Retention",
    level: 3,
    isPolicy: true,
  },
  {
    id: "third-party-services",
    title: "5. Third-Party Services",
    level: 3,
    isPolicy: true,
  },
  { id: "your-rights", title: "6. Your Rights", level: 3, isPolicy: true },
  { id: "contact-privacy", title: "7. Contact", level: 3, isPolicy: true },
];

export default function LegalPage() {
  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map((section) => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find((section) => {
        if (!section.element) return false;
        const rect = section.element.getBoundingClientRect();
        return rect.top <= 150 && rect.bottom > 0;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once on mount to set initial active section

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground w-screen">
      <div className="fixed bottom-0 right-0 m-4">
        <ModeToggle />
      </div>
      <header className="py-12 mt-4 flex justify-center gap-2 scale-[1.35] w-full">
        <Link
          href="/"
          className="flex items-center gap-2 self-center font-medium"
          passHref
          draggable={false}
        >
          <Button
            variant={"ghost"}
            className="w-min px-2 pr-3 py-6 rounded-full"
          >
            <div className="flex aspect-square size-8 relative">
              <Image src="/logo/icon.svg" alt={"Logo"} fill draggable={false} />
            </div>
            <h3 className={`text-2xl font-bold ${montserrat.className}`}>
              Brief
            </h3>
          </Button>
        </Link>
      </header>
      <div className="flex flex-col md:flex-row max-w-7xl mx-auto px-8 gap-8">
        <main className="w-full md:w-3/4 pb-8 mt-4 md:pr-8">
          <div className="prose dark:prose-invert mx-auto max-w-none">
            <h2 id="terms-and-conditions">Terms and Conditions</h2>
            <p>
              <strong>Effective Date: May 18, 2025</strong>
            </p>
            <p>
              Welcome to BriefQB, an AI-powered platform designed to deliver
              personalized, real-time news briefs on trending topics. These
              Terms of Service (“Terms”) govern your access to and use of
              BriefQB (“we”, “us”, or “our”), including all features, content,
              and services offered through the platform.
            </p>
            <p>
              BriefQB is developed and owned by Ayaan Zaveri and Zain Pirani,
              both of whom are under the age of majority. As such, legal
              commitments on behalf of BriefQB may require guardian consent. If
              you are under 18, please have a parent or legal guardian review
              these Terms before using the platform.
            </p>

            <h3 id="acceptance-of-terms">1. Acceptance of Terms</h3>
            <p>
              By accessing or using BriefQB, you agree to comply with and be
              bound by these Terms. If you do not agree to these Terms, you may
              not access or use the platform.
            </p>
            <p>
              We reserve the right to update or modify these Terms at any time,
              and such changes will be effective immediately upon posting. Your
              continued use of the platform constitutes acceptance of any
              updates. You are encouraged to review these Terms periodically.
            </p>

            <h3 id="user-responsibilities">2. User Responsibilities</h3>
            <p>By using BriefQB, you agree to the following:</p>
            <ul>
              <li>
                You will use the platform only for lawful, personal, and
                non-commercial purposes.
              </li>
              <li>
                You will not misuse, abuse, exploit, or reverse-engineer any
                part of the service.
              </li>
              <li>
                You will not engage in activities such as spamming, unauthorized
                scraping, or using bots or automation to access or manipulate
                the service.
              </li>
              <li>
                You will not publish or distribute BriefQB-generated content
                without proper credit and context.
              </li>
              <li>
                You are solely responsible for your actions while using the
                platform.
              </li>
            </ul>

            <h3 id="subscription-plans">3. Subscription Plans and Payments</h3>
            <p>BriefQB offers two subscription tiers:</p>
            <ul>
              <li>
                <strong>Free Plan – $0/month</strong>
                <ul>
                  <li>Limited audio plays (5/day)</li>
                  <li>Basic access only</li>
                  <li>Fixed playback speed</li>
                </ul>
              </li>
              <li>
                <strong>Pro Plan – $4.99/month</strong>
                <ul>
                  <li>Unlimited audio plays</li>
                  <li>Unlimited audio generations</li>
                  <li>Access to detailed transcripts</li>
                  <li>Adjustable playback speed</li>
                </ul>
              </li>
            </ul>
            <p>
              Subscriptions are billed monthly via Stripe and renew
              automatically unless canceled. You may cancel your subscription
              anytime through your Stripe customer portal. Refunds are not
              issued unless otherwise required by applicable law.
            </p>

            <h3 id="account-termination">4. Account Termination</h3>
            <p>
              We reserve the full and sole right to suspend, restrict, or
              permanently terminate any user account at our discretion, for any
              reason we determine to be justified, including but not limited to:
            </p>
            <ul>
              <li>Violation of these Terms</li>
              <li>Suspected or actual misuse of the service</li>
              <li>Conduct harmful to other users or BriefQB’s reputation</li>
              <li>Abusive or fraudulent behavior</li>
            </ul>
            <p>
              Termination may occur with or without prior notice. Upon
              termination, all access to the platform and its content will
              cease. We may also permanently delete your data from our records.
            </p>

            <h3 id="intellectual-property">5. Intellectual Property</h3>
            <p>
              All content, including AI-generated news summaries, curated
              materials, platform design, branding, and user interface elements,
              are the exclusive property of BriefQB or its licensors.
              Unauthorized copying, redistribution, or derivative use of the
              content is strictly prohibited.
            </p>
            <p>
              Content gathered from public news sources and summarized using
              artificial intelligence is protected under fair use. If your
              content has been used and you believe it violates your rights,
              please contact us with proof of identity and ownership.
            </p>

            <h3 id="privacy-and-data-use-terms">6. Privacy and Data Use</h3>
            <p>
              Your privacy is important to us. By using BriefQB, you agree to
              the collection and use of your personal information in accordance
              with our Privacy Policy.
            </p>
            <p>
              We do not sell your personal data, and all data collection is used
              solely for improving your user experience and maintaining platform
              functionality.
            </p>

            <h3 id="limitation-of-liability">7. Limitation of Liability</h3>
            <p>
              BriefQB is provided “as is” and without warranties, express or
              implied. We do not guarantee that the service will be
              uninterrupted, secure, or error-free.
            </p>
            <p>
              To the fullest extent permitted by law, BriefQB and its owners are
              not liable for any damages, direct or indirect, arising from the
              use or inability to use the platform, including data loss, service
              outages, or loss of revenue.
            </p>

            <h3 id="modifications-and-availability">
              8. Modifications and Availability
            </h3>
            <p>
              We reserve the right to modify, suspend, or discontinue the
              platform or any feature thereof at any time without notice.
              BriefQB shall not be liable for any disruption or unavailability
              of the platform.
            </p>

            <h3 id="waiver-and-arbitration">
              9. Waiver of Right to Sue & Binding Arbitration
            </h3>
            <p>
              By using BriefQB, you expressly waive your right to file a lawsuit
              in any court or participate in a class action lawsuit against
              BriefQB, its owners, developers, or affiliates.
            </p>
            <p>
              Instead, you agree that any dispute, claim, or controversy arising
              out of or relating to these Terms or your use of BriefQB shall be
              resolved through final and binding arbitration administered by a
              mutually agreed-upon arbitrator, conducted in accordance with
              applicable laws.
            </p>
            <p>
              This clause applies to all legal theories, including breach of
              contract, tort, statute, or regulation. You agree that any
              arbitration shall be conducted individually and not as part of a
              class or consolidated action.
            </p>

            <h3 id="contact-information-terms">10. Contact Information</h3>
            <p>
              If you have any questions regarding these Terms, you may contact
              us at:
            </p>
            <p>
              <a href="mailto:briefqb@gmail.com">briefqb@gmail.com</a>
            </p>

            <hr className="my-12" />

            <h2 id="privacy-policy">Privacy Policy</h2>
            <p>
              <strong>Effective Date: May 18, 2025</strong>
            </p>
            <p>
              At BriefQB, protecting your privacy is our priority. This Privacy
              Policy explains what personal information we collect, how it is
              used, and the steps we take to protect it.
            </p>

            <h3 id="information-we-collect">1. Information We Collect</h3>
            <p>We may collect the following types of data:</p>
            <ul>
              <li>
                Personal Information: Name, email address, and account details
              </li>
              <li>
                Usage Data: Interaction with the app, topics searched, frequency
                of use
              </li>
              <li>Subscription & Billing Info: For Pro users via Stripe</li>
            </ul>
            <p>
              This data helps us optimize your experience, deliver tailored news
              content, and provide technical support.
            </p>

            <h3 id="payment-processing">2. Payment Processing</h3>
            <p>
              All payments are securely handled by Stripe. We do not store or
              access any credit card information.
            </p>
            <p>
              For details on how Stripe processes your payment and protects your
              data, visit their Privacy Policy at:
              <br />
              {" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://stripe.com/privacy
              </a>
            </p>

            <h3 id="cookies-and-tracking">3. Cookies and Tracking</h3>
            <p>We may use cookies and similar technologies to:</p>
            <ul>
              <li>Remember user preferences</li>
              <li>Track analytics and performance</li>
              <li>Personalize content and suggestions</li>
            </ul>
            <p>
              You can disable cookies via your browser settings at any time.
            </p>

            <h3 id="data-retention">4. Data Retention</h3>
            <p>
              We retain your personal information only for as long as is
              necessary to fulfill the purposes outlined in this Privacy Policy.
              If you choose to delete your account, we will erase all associated
              data unless required by law to retain it longer.
            </p>

            <h3 id="third-party-services">5. Third-Party Services</h3>
            <p>
              We use trusted third-party services (e.g., Stripe, Firebase,
              analytics platforms) that may process certain user data. These
              providers are required to maintain strict confidentiality and
              comply with applicable privacy laws.
            </p>

            <h3 id="your-rights">6. Your Rights</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Access the data we hold about you</li>
              <li>Request correction or deletion of your data</li>
              <li>Withdraw consent for email communication</li>
              <li>Request account deletion at any time</li>
            </ul>
            <p>
              Please email{" "}
              <strong>
                <a href="mailto:briefqb@gmail.com">briefqb@gmail.com</a>
              </strong>{" "}
              to exercise your rights.
            </p>

            <h3 id="contact-privacy">7. Contact</h3>
            <p>
              If you have any questions or concerns regarding this Privacy
              Policy or how your data is handled, please contact us:
            </p>
            <p>
              <a href="mailto:briefqb@gmail.com">briefqb@gmail.com</a>
            </p>
          </div>
        </main>
        <aside className="w-full md:w-1/3 hidden lg:block p-4 md:sticky md:top-20 self-start mb-8 md:mb-0">
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Table of Contents</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <nav className="space-y-1.5">
                {sections.map((section) => (
                  <Link
                    key={section.id}
                    href={`#${section.id}`}
                    className={`block text-sm transition-colors ${
                      section.level === 3 ? "ml-4" : "font-medium"
                    } ${section.isPolicy && section.level === 2 ? "mt-4" : ""} ${
                      activeSection === section.id
                        ? "text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => {
                      setActiveSection(section.id);
                    }}
                  >
                    {section.title}
                  </Link>
                ))}
              </nav>
            </CardContent>
          </Card>
        </aside>
      </div>
      <footer className="py-8 text-center text-muted-foreground pb-12">
        <p>&copy; {new Date().getFullYear()} BriefQB. All rights reserved.</p>
      </footer>
    </div>
  );
}
